import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { RefreshCw, Tool } from 'react-feather'

import './maintenance-overlay.scss'

// El estado se edita desde el backend (PATCH /maintenance en Swagger): cambiarlo no requiere deploy.
const STATUS_URL = `${process.env.REACT_APP_BACKEND_URL}/maintenance`
const POLL_INTERVAL_MS = 60000
const STORAGE_KEY = 'tifi-maintenance'

const DEFAULT_TEXTS = {
  badge: 'Mejoras en progreso',
  title: 'Estamos mejorando',
  titleHighlight: 'para ti',
  message: 'El servicio de Tifi está temporalmente en mantenimiento. Nuestro equipo trabaja para ofrecerte una mejor experiencia.',
  closing: '¡Volveremos pronto!',
  status: 'Actualizando nuestros servicios'
}

const fetchMaintenance = async () => {
  const response = await fetch(STATUS_URL, { cache: 'no-store' })
  if (!response.ok) throw new Error(`HTTP ${response.status}`)
  const data = await response.json()
  return data.enabled === true ? data : null
}

const readLastKnown = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY))
  } catch {
    return null
  }
}

const MaintenanceOverlay = () => {
  // Si la API se apaga durante el mantenimiento, recargar la página no debe quitar el aviso.
  const [config, setConfig] = useState(readLastKnown)
  const active = config !== null

  useEffect(() => {
    let cancelled = false

    const check = async () => {
      try {
        const next = await fetchMaintenance()
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        if (!cancelled) setConfig(next)
      } catch {
        // API caída o sin red: no se sabe el estado real, se conserva el último conocido.
      }
    }

    check()
    const interval = setInterval(check, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      clearInterval(interval)
    }
  }, [])

  // La app sigue montada debajo (formularios y requests en curso no se pierden), pero no recibe foco ni scroll.
  useEffect(() => {
    if (!active) return
    const root = document.getElementById('root')
    root.setAttribute('inert', '')
    document.body.style.overflow = 'hidden'
    return () => {
      root.removeAttribute('inert')
      document.body.style.overflow = ''
    }
  }, [active])

  if (!active) return null

  const texts = { ...DEFAULT_TEXTS, ...config }

  return createPortal(
    <div className='tifi-maintenance' role='alertdialog' aria-modal='true' aria-labelledby='tifi-maintenance-title'>
      <span className='tifi-maintenance__logo'>Tifi</span>

      <main className='tifi-maintenance__content'>
        <section className='tifi-maintenance__text'>
          <span className='tifi-maintenance__badge'>
            <Tool size={18} aria-hidden='true' />
            {texts.badge}
          </span>
          <h1 id='tifi-maintenance-title' className='tifi-maintenance__title'>
            {texts.title} <em>{texts.titleHighlight}</em>
          </h1>
          <p className='tifi-maintenance__message'>
            {texts.message} <strong>{texts.closing}</strong>
          </p>
          <div className='tifi-maintenance__status' role='status'>
            <span className='tifi-maintenance__spinner' aria-hidden='true'>
              <RefreshCw size={22} />
            </span>
            {texts.status}
          </div>
          <div className='tifi-maintenance__progress' aria-hidden='true'>
            <span />
          </div>
        </section>

        <figure className='tifi-maintenance__media'>
          <img
            src={`${process.env.PUBLIC_URL}/assets/maintenance/team.webp`}
            alt='Equipo de Tifi trabajando en mejoras de la plataforma'
          />
          <i className='tifi-maintenance__sparkle tifi-maintenance__sparkle--a' aria-hidden='true' />
          <i className='tifi-maintenance__sparkle tifi-maintenance__sparkle--b' aria-hidden='true' />
        </figure>
      </main>

      <footer className='tifi-maintenance__footer'>
        <strong>© Tifi Entregas</strong> · Gracias por tu paciencia
      </footer>
    </div>,
    document.body
  )
}

export default MaintenanceOverlay
